import type { InjectionKey } from 'vue';

/** What a menu panel hands its items: a way to close the whole menu. */
export interface NxMenuContext {
  /** Closes the menu (and every submenu) — an item calls it after `select`. */
  close: () => void;
}

export const nxMenuContextKey: InjectionKey<NxMenuContext> = Symbol('nx-menu-context');

/** Items of `panel` itself (not of a nested submenu panel) that can take focus. */
export function menuItemsOf(panel: HTMLElement | null | undefined): HTMLElement[] {
  if (!panel) return [];
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
    )
  ).filter(
    (el) =>
      el.closest('[role="menu"]') === panel &&
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-disabled') !== 'true'
  );
}

/**
 * Moves focus between the items of `panel` for ArrowUp/ArrowDown (wrapping),
 * Home and End. Returns whether the key was handled.
 */
export function moveMenuFocus(panel: HTMLElement | null | undefined, key: string): boolean {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return false;
  const items = menuItemsOf(panel);
  if (!items.length) return true;
  const current = items.indexOf(document.activeElement as HTMLElement);
  let next: number;
  if (key === 'Home') next = 0;
  else if (key === 'End') next = items.length - 1;
  else if (current === -1) next = key === 'ArrowDown' ? 0 : items.length - 1;
  else next = (current + (key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
  items[next]?.focus();
  return true;
}

/**
 * Puts `el` into (or out of) the popover top layer, so a menu sits above every
 * dialog and drawer without a z-index. Guarded: jsdom and old engines have no
 * `showPopover`.
 */
export function setTopLayer(el: HTMLElement | null | undefined, open: boolean): void {
  if (!el || !('showPopover' in el)) return;
  try {
    if (open) el.showPopover();
    else el.hidePopover();
  } catch {
    /* already in the requested state */
  }
}
